/* header.js - the header's module, the only component script loaded eagerly.
   iOS has no scroll anchoring, so the header box must measure the same at rest
   and scrolled: the collapsing row moves by opacity (and above 1024px by a
   transform as well), its height is pinned, and since neither takes it out of
   the tab order it also takes `inert`. The row leaves on a downward gesture and
   COMES BACK ON AN UPWARD ONE from any depth (apply() says what a gesture is),
   not only once the page is back at the top. init() re-runs on
   shopify:section:load, which the editor needs and never triggers by itself.

   WHICH ROW COLLAPSES IS READ OFF THE BOXES, not off a breakpoint repeated here.
   From 1024px the navigation row is rendered and it is the header's lowest row,
   so it is the one that leaves. On a phone that row is display:none and the row
   under the icons is the search field, so the search leaves and the icon row
   stays, which is how a phone gives screen height back at all: before this the
   header painted the same 139px at every scroll position there.

   Two classes go on the SECTION wrapper for the stylesheet to read, because the
   thing that changes state and the thing that has to be painted are siblings and
   CSS cannot look sideways: `is-scrolled` says the header has left its resting
   place and content is now running under it, `is-nav-visible` says the
   navigation row is still the header's lowest visible row. */
(function () {
  'use strict';

  var PP = window.PP;
  if (!PP) return;

  var root = document.documentElement;
  var cleanup = [];
  var active = null;

  function on(t, type, fn, opts) {
    t.addEventListener(type, fn, opts);
    cleanup.push(function () { t.removeEventListener(type, fn, opts); });
  }

  function destroy() {
    while (cleanup.length) cleanup.pop()();
    active = null;
  }

  /* The topbar is its own section and outlives a removed header. There is more
     than one selector on every route now, the top bar's and the footer's, so
     EVERY hook is armed rather than the first one found: arming only the first
     left the footer's submit button visible and its change handler missing.
     data-armed stays the idempotence latch, so a re-init cannot double-bind. */
  function armLocalization() {
    var boxes = document.querySelectorAll('[data-localization]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      if (box.hasAttribute('data-armed')) continue;
      box.setAttribute('data-armed', '');
      box.addEventListener('change', function (event) {
        var f = event.target;
        if (f && f.hasAttribute('data-localization-select') && f.form) f.form.submit();
      });
    }
  }

  function init(scope) {
    var header = (scope || document).querySelector('[data-header]');
    if (!header) return;
    destroy();
    active = header;

    var section = header.closest('.header-section') || header.parentElement;
    var nav = header.querySelector('[data-header-nav]');
    var logo = header.querySelector('[data-header-logo]');
    var searchRow = header.querySelector('[data-header-search]');
    var searchToggle = header.querySelector('[data-header-search-toggle]');
    var collapse = header.getAttribute('data-collapse') === 'true';
    var items = nav ? [].slice.call(nav.querySelectorAll('[data-nav-details]')) : [];
    var hoverable = window.matchMedia('(hover: hover)').matches;
    var baseline = 0;
    var scrolled = false;
    var ticking = false;
    var forced = false;
    /* THE COLLAPSE FOLLOWS THE DIRECTION OF TRAVEL, NOT ONLY THE DEPTH. Until
       2026-09-12 the row left the moment the page passed the header's own
       height and came back only once the page was above it again, so from
       any depth a shopper scrolling UP, which is the gesture that means "I want
       the navigation", got nothing back until the very top (measured on the
       served theme at 1440x900: 900px down then 150px up, row still at opacity
       0; only at scrollY 150 did it return). Now the row returns after
       REVEAL_PX of continuous upward travel and leaves again after the same
       amount downward, and the depth test only decides the resting case: above
       the baseline the row is always shown, whatever the direction.
       24px is the smallest number that ignores every reversal that is not a
       gesture: a trackpad's inertial tail reverses by a pixel or three as it
       settles, a thumb lifting off a touch screen by around ten, and a single
       notch of a mouse wheel is a hundred in Chromium, so one notch up is
       enough to bring the row back. The travel is a running total in ONE
       direction and resets the instant the sign flips, so ten jittery frames
       of 3px cannot add up to a gesture nobody made. */
    var REVEAL_PX = 24;
    var lastY = window.pageYOffset;
    var travel = 0;
    var heading = 0;
    /* The row a collapse actually moves, settled by measure() because it is a
       fact about the rendered boxes and a resize can change the answer. */
    var collapsing = null;
    /* -1, not 0: a header whose edge really is at 0 (pinned, no topbar) must
       still get its first write, and 0 would swallow it as "unchanged". */
    var edge = -1;
    /* The boxes inside the header that carry a background of their own, settled
       by measure() because which of them exists is a fact about the format, and
       how far the lowest of them reaches below the header's top. */
    var surfaces = [];
    var edgeOffset = 0;
    /* The handle of the frame loop that follows a fade; 0 when none runs. */
    var edgeTick = 0;
    /* The same floor the two header benches use: below 5% of effective opacity a
       box is in the tree and not on the screen, so it cannot be the edge. */
    var PAINT_FLOOR = 0.05;
    /* null rather than false, for the same reason and a sharper case: a reload
       restores the scroll position, so the very first frame can already be deep
       in the document, and `false` would read that as "unchanged" and leave the
       header painted as if it were resting at the top of the page. */
    var pinned = null;
    var sticky = !!section && header.getAttribute('data-sticky') === 'true';

    if (sticky) section.classList.add('header-sticky');

    /* Set once and only ever cleared by the collapse below, so a header with no
       navigation row at all never carries it. That is the case the class exists
       to separate: without it the stylesheet would have to ask "is the row
       hidden", which is also true when the row was never rendered, and a store
       with an empty menu would lose its bottom edge entirely. */
    if (section && nav) section.classList.add('is-nav-visible');

    function closeAll(except) {
      for (var i = 0; i < items.length; i++) {
        if (items[i] !== except) items[i].open = false;
      }
    }

    /* THE HEADER'S LOWEST ROW, ASKED OF THE LAYOUT. Repeating the stylesheet's
       1024px here would be two places to change and one of them would be
       forgotten; the boxes already know. The navigation row is rendered only
       from 1024px, so its display settles the desktop answer outright. Below it
       the search field is the row under the icons, and the geometric test is
       what keeps the one case that looks like a phone but is not: a desktop
       store with an empty menu renders no navigation row at all, and there the
       field SHARES the icon row, so collapsing it would empty the header's only
       row instead of giving a second one back. */
    function lowestRow() {
      if (nav && getComputedStyle(nav).display !== 'none') return nav;
      if (!searchRow || !logo) return null;
      var below = searchRow.getBoundingClientRect().top >= logo.getBoundingClientRect().bottom - 0.5;
      return below ? searchRow : null;
    }

    /* A row leaves by opacity, and above 1024px by a transform as well, never by
       a height: the header box is what must not move. Neither of those takes the
       row out of the tab order or the accessibility tree, so `inert` does. */
    function setRow(row, hidden) {
      row.classList.toggle('is-hide', hidden);
      if (hidden) {
        row.setAttribute('inert', '');
        if (row === nav) closeAll(null);
      } else {
        row.removeAttribute('inert');
      }
    }

    /* Do not take a row away from under the shopper's hands. On a phone the row
       that leaves is the search row and its results are a fixed sheet rendered
       inside it, so hiding it mid-search would take their own open sheet with
       it; the same guard keeps a focused menu item from being made inert under
       the keyboard. Read as a state of the row rather than as a component's
       class name, so nothing here has to know what is mounted inside it. */
    function busy(row) {
      if (row.contains(document.activeElement)) return true;
      return !!row.querySelector('.is-open');
    }

    /* Two different numbers, and confusing them is a real defect this theme
       already shipped. --pp-header-height is how TALL the header is; it is what
       something anchoring to the band the header occupies once it is pinned
       wants (scroll-padding, scroll-margin, a sticky column's offset), because
       by then whatever sat above the header has scrolled away.
       --pp-header-bottom is WHERE the header's lower edge is on screen right
       now, which is what a fixed overlay opening under the header wants. At
       rest they differ by everything stacked above the header: the topbar
       measures 42px on this store, so the search sheet anchored to the height
       opened 42px too high and covered 25.6px of a 48px search field (measured
       on the served theme, 390x844, before this change). It follows the scroll
       because that gap closes as the topbar leaves. */
    /* THE PAINT, NOT THE BOX, and the difference only appeared the day this
       header started compacting. The box bottom used to be the lower edge
       because the header painted its whole reserved height; now the reserved
       height stays in the flow (that is the iOS guard) while the painted bar
       shrinks above it, so the box bottom answers 139px on a phone and 148px
       above 1024px when the visible edge is at 78.75px and 104px (measured on
       the served theme). An overlay anchored to the box bottom would float 60px
       or 44px below the bar with page showing through the gap.

       The surfaces are COLLECTED FROM THE RENDERED DOCUMENT rather than named
       here: any box in the header carrying a background of its own is a
       candidate, whichever wrapper a later repair moves the paint onto. Naming
       them is exactly the mistake the two header benches were just repaired for.

       A background is a fact about the stylesheet and the format, so the
       candidate list is settled by measure(); which candidates are SHOWING is a
       fact about the state and the fade, so that is settled per frame. */
    function carriesPaint(cs) {
      if (cs.backgroundImage && cs.backgroundImage !== 'none') return true;
      var m = String(cs.backgroundColor || '').match(/^rgba?\(([^)]+)\)/);
      if (!m) return false;
      var parts = m[1].split(/[,\s/]+/);
      return parts.length < 4 || parseFloat(parts[3]) > 0;
    }

    function collectSurfaces() {
      var all = header.querySelectorAll('*');
      surfaces = [];
      for (var i = -1; i < all.length; i++) {
        var el = i < 0 ? header : all[i];
        var cs = getComputedStyle(el);
        if (cs.display === 'none') continue;
        if (carriesPaint(cs)) surfaces.push(el);
      }
    }

    /* A row leaves by opacity, so its paint is real until its opacity is. The
       product is taken up the chain and stops at the header: a row at opacity 1
       inside a wrapper at 0 paints nothing, and reading only the row's own
       opacity would keep the edge at a strip nobody can see.
       THE ENGINE IS ASKED FIRST, because the opacity chain is not enough on its
       own. checkVisibility also knows about content SKIPPED by an ancestor's
       content-visibility, and that is not a detail in this header:
       snippets/mega-menu.liquid parks its closed panels by flipping
       ::details-content to hidden, which FREEZES their fade part-done, so at rest
       this header holds a 303px panel with a computed opacity of 0.87 that paints
       nothing whatsoever. Read through opacities alone, that panel published the
       edge at 480px -- 291px below the header (measured on the served theme,
       1440x900, 2026-09-11). */
    function showing(el) {
      if (el.checkVisibility
        && !el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true, contentVisibilityAuto: true })) return 0;
      var node = el;
      var product = 1;
      while (node && node !== header.parentElement) {
        var cs = getComputedStyle(node);
        if (cs.display === 'none' || cs.visibility !== 'visible') return 0;
        /* A FIXED BOX, OR ANYTHING INSIDE ONE, IS ANCHORED TO THE VIEWPORT AND
           NOT TO THE HEADER, and letting one in would not just be wrong, it would
           feed back. The search results sheet is fixed and reads
           `inset-block: max(var(--pp-header-bottom), 5.6rem) 0`, so it stretches
           to the bottom of the screen; its own children are NOT fixed, so testing
           only the candidate's own position let a child publish 844px as the
           header's edge, which the sheet then reads to place itself. The two would
           chase each other down the screen. The test is the whole chain for that
           reason. */
        if (cs.position === 'fixed') return 0;
        var v = parseFloat(cs.opacity);
        product *= isFinite(v) ? v : 1;
        node = node.parentElement;
      }
      return product;
    }

    function pickEdge(box) {
      var low = null;
      for (var i = 0; i < surfaces.length; i++) {
        if (showing(surfaces[i]) <= PAINT_FLOOR) continue;
        var b = surfaces[i].getBoundingClientRect().bottom;
        if (low === null || b > low) low = b;
      }
      /* Nothing painting at all is not an invitation to anchor overlays at the
         top of the header: fall back to the box bottom, which is what this
         token published before and is never above the paint. */
      edgeOffset = (low === null ? box.bottom : low) - box.top;
    }

    /* ONE rect for the two answers a scroll frame needs. Both the edge and the
       pinned test come out of the same box, so taking a second
       getBoundingClientRect() would buy a second forced layout for a number
       already in hand; the surfaces are only re-read when `repick` says the
       paint itself can have moved, which is never on a plain scroll frame.
       doc: shopify.dev/docs/storefronts/themes/best-practices/performance/debounce-throttle-event-handlers */
    function publishEdge(repick) {
      var box = header.getBoundingClientRect();
      if (repick) pickEdge(box);
      /* CEILING, NOT ROUNDING. This number is where an overlay's top edge goes, so
         a half-pixel error has a right side and a wrong side: rounded down it sits
         ON the paint (measured mid-fade at 1440x900, token 147 against a painted
         bottom of 147.11), rounded up it sits a fraction of a pixel below it,
         which is invisible. Every whole-pixel state reads the same either way. */
      var bottom = Math.ceil(box.top + edgeOffset);
      if (bottom !== edge) {
        edge = bottom;
        root.style.setProperty('--pp-header-bottom', bottom + 'px');
      }
      /* An unpinned header scrolls away with the page, so nothing ever runs
         under it and it has no edge to draw. */
      if (!sticky) return;
      /* At or below zero, and only once the page has moved. The sticky box is
         the SECTION wrapper at inset-block-start 0 and the header is its first
         child, so a pinned header rests at EXACTLY 0 and never below it: the
         former `< 0` was a test nothing could pass, and the pinned edge never
         lit on any route (measured on the served theme at 390x844 and
         1440x900, header rect top 0 at scrollY 1200). The pageYOffset half is
         what keeps a store with no top bar, whose header rests at 0 before a
         single pixel of content has passed underneath, from lighting up at
         rest. Deliberately not rounded: the sub-pixel offsets a trackpad
         produces are real scrolling, and rounding them to 0 would hold the
         edge back for a frame or two. */
      var next = box.top <= 0 && window.pageYOffset > 0;
      if (next === pinned) return;
      pinned = next;
      section.classList.toggle('is-scrolled', next);
    }

    /* WHY A FRAME LOOP AND NOT THE TWO ENDS OF THE COLLAPSE. Publishing the edge
       when the state flips and again when it has settled would leave the token
       wrong for the whole length of the fade, and an overlay opened in that
       window -- the account dialog is one, and it can be opened while the header
       is compacted -- would anchor into a strip the header is still painting.
       The fade is a CSS transition, so the only way to read its progress is a
       frame at a time. This loop runs ONLY while one is in flight, never on the
       scroll path, and ends by itself as soon as the row has reached the opacity
       its class asks for. No duration is read or repeated here, which is what
       makes it right under prefers-reduced-motion too: the first frame already
       finds the row settled and the loop stops after one pass. */
    function trackFade() {
      if (edgeTick) return;
      var frames = 0;
      var step = function () {
        edgeTick = 0;
        if (active !== header) return;
        publishEdge(true);
        if (!collapsing) return;
        var op = parseFloat(getComputedStyle(collapsing).opacity);
        var target = collapsing.classList.contains('is-hide') ? 0 : 1;
        frames += 1;
        /* 120 frames is not a duration, it is a leash. A transition CAN stay
           unfinished on this theme -- snippets/mega-menu.liquid parks its closed
           panels mid-fade by flipping ::details-content to content-visibility:
           hidden, which freezes the animation inside -- and a loop waiting on a
           frozen opacity would run for the rest of the session. */
        if (Math.abs(op - target) <= 0.01 || frames > 120) return;
        edgeTick = requestAnimationFrame(step);
      };
      edgeTick = requestAnimationFrame(step);
    }

    function measure() {
      header.style.minHeight = '';
      var h = Math.round(header.getBoundingClientRect().height);
      if (!h) return;
      header.style.minHeight = h + 'px';
      root.style.setProperty('--pp-header-height', h + 'px');
      if (nav) {
        var n = Math.round(nav.getBoundingClientRect().height);
        header.style.setProperty('--pp-header-nav-height', n + 'px');
      }
      /* A resize across the breakpoint hands the collapse to the other row, and
         the row that is no longer the collapsing one has to be given back
         whatever an earlier scroll did to it: a phone rotated to tablet width
         would otherwise keep a search field nobody can see or reach. */
      var previous = collapsing;
      collapsing = lowestRow();
      if (previous && previous !== collapsing) setRow(previous, false);
      if (section && !collapsing) section.classList.remove('is-collapsed');
      baseline = (section ? section.offsetTop : 0) + h;
      /* After the row swap above, and before the edge is published: a resize
         across the breakpoint changes which boxes paint at all (below 1024px the
         icon row is its own painted box and the row that wrapped it paints
         nothing), so the candidate list has to be rebuilt before it is read. */
      collectSurfaces();
      publishEdge(true);
      trackFade();
    }

    function apply() {
      ticking = false;
      /* Ahead of the early return below on purpose. That return fires on almost
         every scroll frame, because it only cares whether the collapsed/expanded
         state flipped, and the edge moves on frames where that state does not. */
      publishEdge();
      var y = window.pageYOffset;
      var delta = y - lastY;
      lastY = y;
      if (delta !== 0) {
        var sign = delta > 0 ? 1 : -1;
        travel = sign === heading ? travel + Math.abs(delta) : Math.abs(delta);
        heading = sign;
      }
      /* Three answers. Above the baseline the row is simply there, which is the
         resting page and the state both header benches read back at y=0.
         Below it the direction decides, and only once the travel is a gesture:
         anything shorter keeps whatever the row was doing, so the first frames
         of a reversal never flicker it. A reload that lands deep in the
         document has no travel at all and so keeps the row it was served with,
         which is the visible one: the shopper has not scrolled yet. */
      var next;
      if (y <= baseline) next = false;
      else if (travel >= REVEAL_PX) next = heading > 0;
      else next = scrolled;
      /* THE ROW'S OWN STATE IS PART OF THE TEST, not just the threshold. The
         magnifier at the bottom of this file puts the row back by hand with the
         page still deep in the document, so the threshold has not flipped and a
         test that read only the threshold would never notice that the row has to
         be taken away again. Asking the row what it is doing is also what makes
         that gesture a peek rather than a state to track. */
      var settled = !collapse || !collapsing || collapsing.classList.contains('is-hide') === next;
      if (next === scrolled && settled && !forced) return;
      if (!collapse || !collapsing) {
        forced = false;
        scrolled = next;
        return;
      }
      /* Deliberately BEFORE `scrolled` is committed, so the collapse is retried
         on the next frame instead of being recorded as done and skipped until
         the shopper has scrolled all the way back up and down again. */
      if (next && busy(collapsing)) return;
      forced = false;
      scrolled = next;
      setRow(collapsing, next);
      if (section) {
        /* What the stylesheet reads to move the edge and to put the magnifier on
           screen. Written whichever row collapsed, so a store with an empty menu
           still gets it on a phone, where there is no navigation row to speak
           for the state. */
        section.classList.toggle('is-collapsed', next);
        /* is-nav-visible keeps exactly the meaning it had: the navigation row is
           the header's lowest visible row. It is written only where that row is
           rendered at all, so an empty menu never claims a row it does not have. */
        if (nav) section.classList.toggle('is-nav-visible', !next);
      }
      /* The class is on, the fade has not started: the edge is republished from
         the paint on this frame and then on every frame of the transition. */
      trackFade();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    }

    function remeasure() {
      if (active !== header) return;
      measure();
      forced = true;
      apply();
    }

    /* A section reload leaves a pending frame behind, and it would write the old
       header's edge over the new one's. destroy() runs these before init(). */
    cleanup.push(function () {
      if (edgeTick) cancelAnimationFrame(edgeTick);
      edgeTick = 0;
    });

    remeasure();
    on(window, 'scroll', onScroll, { passive: true });
    on(window, 'resize', PP.debounce(remeasure, 150));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remeasure);

    items.forEach(function (item) {
      var enterTimer;
      var leaveTimer;
      item.addEventListener('toggle', function () {
        if (item.open) closeAll(item);
      });
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) item.open = false;
      });
      if (!hoverable) return;
      item.addEventListener('mouseenter', function () {
        clearTimeout(leaveTimer);
        enterTimer = setTimeout(function () { item.open = true; }, 100);
      });
      item.addEventListener('mouseleave', function () {
        clearTimeout(enterTimer);
        leaveTimer = setTimeout(function () { item.open = false; }, 150);
      });
    });

    if (items.length) {
      on(document, 'click', function (event) {
        if (!nav.contains(event.target)) closeAll(null);
      });
      on(document, 'keydown', function (event) {
        if (event.key === 'Escape') closeAll(null);
      });
    }

    var countWrap = header.querySelector('[data-cart-count-wrapper]');
    var countEl = header.querySelector('[data-cart-count]');
    if (countEl) {
      /* The badge pulses on a cart that GREW and on nothing else. Every surface
         that touches the cart publishes here, including a removal, a discount
         and a note autosave, and a badge that bounces when the shopper has just
         emptied a line is feedback contradicting itself. The seed is read off
         the markup the server rendered, so the first add after a page load has
         something honest to compare against.
         Deliberately independent of assets/fly-to-cart.js: that module animates
         a ghost of the packshot and never touches this element, so the two land
         as one gesture rather than as two effects fighting over the same node. */
      var previous = parseInt(countEl.textContent, 10);
      if (isNaN(previous)) previous = 0;
      cleanup.push(PP.subscribe(PP.EVENTS.cartUpdate, function (detail) {
        var n = detail && detail.item_count;
        if (n == null && detail && detail.cart) n = detail.cart.item_count;
        if (n == null) return;
        countEl.textContent = n;
        if (countWrap) countWrap.hidden = n < 1;
        var grew = n > previous;
        previous = n;
        if (!grew || !countWrap) return;
        /* Remove, force the reflow, re-add. A class already on the element does
           not restart its animation, so two adds in quick succession would
           bounce once and then sit still for the second one. */
        countWrap.classList.remove('is-bumped');
        void countWrap.offsetWidth;
        countWrap.classList.add('is-bumped');
      }));
    }

    /* A tap arms the loader through pointerover, consuming the tap's own click,
       so the wish to open is carried as a flag instead. */
    var toggle = header.querySelector('[data-menu-drawer-toggle]');
    var drawerSrc = header.getAttribute('data-drawer-js');
    if (toggle && drawerSrc) {
      var wants = false;
      toggle.addEventListener('click', function () {
        if (typeof PP.openMenuDrawer !== 'function') wants = true;
      });
      PP.onIntent(toggle, drawerSrc, function () {
        if (wants && typeof PP.openMenuDrawer === 'function') {
          wants = false;
          PP.openMenuDrawer();
        }
      });
    }

    var field = header.querySelector('[data-predictive-input]');
    var searchSrc = header.getAttribute('data-search-js');
    if (field && searchSrc) PP.onIntent(field, searchSrc);

    /* THE COLLAPSED PHONE HEADER'S WAY BACK TO THE FIELD. The stylesheet only
       puts this button on screen while the search row is off it, so pressing it
       can mean one thing: hand the row back and put the caret in the field. The
       focus is also what arms the predictive module, through the same onIntent
       the field itself carries. It is a peek rather than a state: the shopper's
       next scroll takes the row away again, which is apply()'s first branch. */
    if (searchToggle && searchRow) {
      on(searchToggle, 'click', function () {
        setRow(searchRow, false);
        if (section) section.classList.remove('is-collapsed');
        /* The row is back in one gesture with no scroll frame behind it, so
           nothing else would republish the edge the sheet anchors to. */
        trackFade();
        if (!field) return;
        /* preventScroll, AND the position put back if the engine ignored it.
           Focusing a control inside a sticky header makes Chromium scroll the
           document to that control's IN FLOW position, which for a pinned header
           is the top of the page: measured on the served theme at 390x844, one
           tap on this button left the page at scrollY 0 having been at 1200. A
           jump to the top is the one thing this header exists not to do, and it
           must not arrive through the control that repairs it. No behavior
           member on the scroll, so the reduced motion cap in base.css governs
           it like every other scroll in this theme. */
        var y = window.pageYOffset;
        field.focus({ preventScroll: true });
        if (window.pageYOffset !== y) window.scrollTo(0, y);
      });
    }
  }

  /* The cart link and the panel it opens belong to two sections that reload
     independently: the link is in the header, the drawer is its own section in
     overlay-group. Arming is therefore a top level concern here rather than a
     line inside init(), so a header rebuilt by the editor cannot un-arm a
     drawer that is already loaded and a drawer dropped in afterwards still
     finds its trigger. With no drawer section on the page the link stays what
     it is in the markup, a plain link to /cart. */
  function armCart() {
    var link = document.querySelector('[data-cart-link]');
    var dialog = document.querySelector('[data-cart-drawer]');
    if (!link || !dialog || link.hasAttribute('data-cart-armed')) return;
    var src = dialog.getAttribute('data-cart-drawer-js');
    if (!src) return;
    link.setAttribute('data-cart-armed', '');

    link.addEventListener('click', function (event) {
      /* A modified click is a request for a second tab, not for the drawer.
         Suppressing it would make the cart the one link in the header that
         cannot be opened alongside the page it was clicked from. */
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
      if (typeof PP.openCartDrawer === 'function') {
        PP.openCartDrawer();
        return;
      }
      /* On touch the tap that arms the loader is the same tap that wanted the
         drawer, and a navigation cannot be undone once it has started. So the
         default is suppressed on every click and /cart is reached only if the
         module genuinely never arrives. */
      PP.loadModule(src).then(
        function () {
          if (typeof PP.openCartDrawer === 'function') PP.openCartDrawer();
          else window.location.href = link.href;
        },
        function () {
          window.location.href = link.href;
        }
      );
    });

    /* Hover, focus or the first touch fetches it before the click lands. */
    PP.onIntent(link, src);
  }

  armLocalization();
  armCart();
  init();

  document.addEventListener('shopify:section:load', function (event) {
    armLocalization();
    armCart();
    if (event.target && event.target.querySelector('[data-header]')) init(event.target);
  });

  document.addEventListener('shopify:section:unload', function () {
    if (active && !active.isConnected) destroy();
  });
})();
